import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import type { ListParticipantsInput, ListParticipantsResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * カード印刷のセッション絞り込み用に、地区の参加者 uid → joinedSessionIds map を取得する hook。
 *
 * カード印刷の「セッション別絞り込み」は post.sessionId ではなく**投稿者の所属セッション**
 * (membership.joinedSessionIds) で判定する。ユーザー設定でチェックしたセッションを基準にしたい、
 * という要望に合わせるため (投稿時の現セッション設定に依存させない)。
 *
 * `listParticipants` callable (master / 担当 district admin) を 1 回呼び、uid→sessions の map を返す。
 * 取得失敗時は空 map (= 全員どのセッションにも未所属扱い、安全側)。
 */
export interface CardPrintParticipantsState {
  /** uid → 所属セッション ID 群。未取得時は空オブジェクト。 */
  authorSessionsByUid: Record<string, string[]>;
  loading: boolean;
  error: Error | null;
}

export function useCardPrintParticipants(districtId: string | null): CardPrintParticipantsState {
  const [state, setState] = useState<CardPrintParticipantsState>({
    authorSessionsByUid: {},
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ authorSessionsByUid: {}, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const fn = httpsCallable<ListParticipantsInput, ListParticipantsResult>(
      functions,
      'listParticipants',
    );
    fn({ districtId })
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, string[]> = {};
        for (const p of res.data.participants ?? []) {
          map[p.uid] = Array.isArray(p.joinedSessionIds) ? p.joinedSessionIds : [];
        }
        setState({ authorSessionsByUid: map, loading: false, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ authorSessionsByUid: {}, loading: false, error: e as Error });
      });
    return () => {
      cancelled = true;
    };
  }, [districtId]);

  return state;
}
